import NavigationLink from "./NavigationLink";
import { ChangeEventHandler, MouseEventHandler, useContext, useEffect, useRef, useState } from "react";
import { FaBars, FaPlusCircle } from "react-icons/fa";
import { useAccount, useIsAuthenticated, useMsal } from "@azure/msal-react";
import Dropdown, { IMenu } from "./ui/Dropdown";
import { BsArrowDownLeftCircle, BsPerson, BsPersonX, BsTruck, BsWifi, BsWifiOff } from "react-icons/bs";
import { GroupsContext } from "../context/GroupsContext";
import { IGroup, RequestState } from "../types/ApiTypes";
import { Link } from "react-router-dom";
import { AppContext } from "../context/AppContext";
import Avatar from "./ui/Avatar";
import { CgSearch } from "react-icons/cg";
import useDebounce from "../hooks/useDebounce";
import { AnimatePresence } from "framer-motion";
import useAuthSocket from "../hooks/useAuthSocket";
import { InteractionStatus } from "@azure/msal-browser";

const bereichMenuItems: IMenu[] = [
    {
        label: <div className='flex items-center'><BsArrowDownLeftCircle className='inline text-xl mr-2'/> Intern</div>,
        href: '/create/intern'
    },
    {
        label: <div className='flex items-center'><BsPerson className='inline text-xl mr-2'/> Kunde</div>,
        href: '/create/customers'
    },
    {
        label: <div className='flex items-center'><BsTruck className='inline text-xl mr-2'/> Lieferant</div>,
        href: '/create/suppliers'
    },
];

const Navigation = () => {
    const searchInputRef = useRef<HTMLInputElement|null>(null);
    const [menuHidden,setMenuHidden] = useState(true);
    const acc = useAccount();
    const navClass = menuHidden ? '-left-full' : 'left-0';
    const hideMenu = () => setMenuHidden(true);
    const showMenu = () => setMenuHidden(false);

    const {connected} = useAuthSocket();
    const isAuthenticated = useIsAuthenticated();

    const [spaces, setSpaces] = useState<IGroup[]>([]);
    const [searchQuery, setSearchQuery] = useState('');

    const {state} = useContext(GroupsContext);
    const appCtx = useContext(AppContext);
    const msal = useMsal();

    const searchForSpaces = (query: string) => {
        return state.groups.filter(x => `${x.type} ${x.name}`.toLowerCase().includes(query.toLowerCase()))
    }
    const debouncedSearchTerm = useDebounce(searchQuery,300);

    useEffect(() => {
        console.log('NAV_GROUP_CHANGED')
        if(debouncedSearchTerm) {
            const spaces = searchForSpaces(debouncedSearchTerm);
            setSpaces(spaces);
        } else {
            setSpaces(state.groups)
        }
    },[searchQuery, debouncedSearchTerm, state.groups])

    const logOut: MouseEventHandler = (e) => {
        e.preventDefault();
        setMenuHidden(true);
        msal.instance.logoutRedirect()
    }

    const onInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        setSearchQuery(e.target.value);
    }

    const onLinkClick = () => {
        if(searchInputRef.current !== null) searchInputRef.current.value = '';
        setMenuHidden(true);
    }

    return (
        <>
            {!menuHidden && <div className="bg-black/80 fixed top-0 left-0 w-full h-full p-4 md:hidden" onClick={hideMenu}/>}
            {menuHidden && <FaBars className='text-3xl md:hidden cursor-pointer mt-5 ml-4' onClick={showMenu}/>}
            <div className={`navigation ${navClass}`}>
                <div className='mx-4 rounded-lg overflow-hidden'>
                    <div className="bg-black/30 p-4 flex justify-between items-center">
                        <FaBars className='text-3xl md:hidden cursor-pointer' onClick={hideMenu}/>
                        <Link to='/' onClick={() => setMenuHidden(true)}>
                            <h1 className='font-bold text-2xl'>SpaceMachine</h1>
                        </Link>
                    </div>
                    <div className="bg-black/20 flex flex-col justify-between gap-2 items-center p-2 lg:p-4">
                        <Avatar big={true}/>
                        <div className="flex flex-col items-center text-sm flex-1 ml-3">
                            <span className="font-semibold">{acc?.name}</span>
                            <div className='flex gap-4'>
                                <a href="/logout.html" onClick={logOut}>Abmelden</a> {appCtx.state.roles.includes('ROLE_ADMIN') && <span>
                                <Link onClick={() => setMenuHidden(true)} to='/admin'>Administration</Link></span>}
                            </div>
                        </div>
                    </div>
                </div>
                <form className='mx-4 mt-2 bg-cardGray border border-slate-700 rounded-md shadow-md flex items-center p-2 space-x-2'>
                    <CgSearch/>
                    <input ref={searchInputRef} className='bg-transparent flex-1 outline-none text-sm' placeholder='Suche nach deinem Space' type="text" onChange={onInputChange}/>
                </form>
                <div className="text-sm sticky top-12">
                    <ul className='max-h-[300px] overflow-y-auto w-full pr-0'>
                        <li className='font-bold text-lg ml-4 mb-2'>Deine Arbeitsbereiche</li>
                        <AnimatePresence>
                            {spaces.map(x => (
                                <NavigationLink
                                    url={`/space/${x.requestId}`}
                                    key={x.requestId}
                                    forced={x.forced}
                                    errored={x.request_state === RequestState.Failed}
                                    loading={x.request_state === RequestState.AwaitsAcceptance || x.request_state === RequestState.Running}
                                    onClick={onLinkClick}
                                >
                                    {x.type} {x.name}
                                </NavigationLink>
                            ))}
                        </AnimatePresence>
                    </ul>
                    <ul>
                        <li className='mx-4 mt-6 font-bold bg-black/10 hover:bg-black/20 transition-colors rounded p-4'>
                            <Dropdown items={bereichMenuItems} triggerElement={<span className='flex gap-2 items-center'><FaPlusCircle className='inline'/> Neues Arbeitsbereich erstellen</span>}/>
                        </li>
                    </ul>
                </div>
                <div className='m-4 mt-auto flex flex-col justify-end justify-self-end sticky bottom-4 items-center'>
                    <span className='rounded-full bg-black/20 p-2 flex gap-2 items-center'>
                        {connected ? <BsWifi className='text-2xl text-success-default'/> : <BsWifiOff className='text-2xl text-error-brighter animate-bounce'/>}
                        {isAuthenticated ? <BsPerson className='text-2xl text-success-default'/> : <BsPersonX className='text-2xl text-error-brighter animate-bounce'/>}
                    </span>
                </div>
            </div>
        </>
    );
}

export default Navigation;